/**
 * Which tokens the current participant controls (may drag / sees through in
 * dynamic fog). Pure function over LIVE inputs — it must never be fed from the
 * join-time snapshot (`mp.serverState`) or the page-store cache
 * (`pages.getPageData(viewingPageId)`): both are frozen after load and were the
 * root of the "works only after F5" class (22.09.2026, docs/ROADMAP.md).
 *
 * Mirrors the server gate in `tokenMoveHandler` (server/src/commands/token.ts):
 * assigned ∪ own-sheet-bound, or everything when the GM granted
 * `playerPermissions.moveOtherTokens`. GM controls everything.
 */

export interface ControlledTokensParticipant {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  readonly userId?: string;
  readonly assignedTokenIds: readonly string[];
}

export interface ControlledTokensSheet {
  readonly id: string;
  readonly ownerName: string | null;
  readonly ownerId?: string | null;
}

export interface ControlledTokensToken {
  readonly id: string;
  readonly characterSheetId?: string;
}

export interface ControlledTokensInput {
  readonly participant: ControlledTokensParticipant | null | undefined;
  readonly sheets: readonly ControlledTokensSheet[];
  readonly tokens: readonly ControlledTokensToken[];
  readonly moveOtherTokens?: boolean;
}

/** True when `sheet` belongs to `participant` — by userId when both sides carry one, else by name. */
export function isSheetOwnedBy(sheet: ControlledTokensSheet, participant: ControlledTokensParticipant): boolean {
  if (participant.userId && sheet.ownerId) return sheet.ownerId === participant.userId;
  return sheet.ownerName === participant.name;
}

export function computeControlledTokenIds(input: ControlledTokensInput): Set<string> {
  const { participant, sheets, tokens, moveOtherTokens } = input;
  const ids = new Set<string>();
  if (!participant) return ids;
  if (participant.role === 'gm' || moveOtherTokens === true) {
    for (const t of tokens) ids.add(t.id);
    return ids;
  }
  const mySheetIds = new Set(sheets.filter(s => isSheetOwnedBy(s, participant)).map(s => s.id));
  for (const t of tokens) {
    if (t.characterSheetId && mySheetIds.has(t.characterSheetId)) ids.add(t.id);
  }
  for (const tid of participant.assignedTokenIds) ids.add(tid);
  return ids;
}

export function sameIdSet(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  if (a.size !== b.size) return false;
  for (const x of a) if (!b.has(x)) return false;
  return true;
}
